/**
 * App.js - Private LLM Chat Frontend
 * Main entry point with routing and authentication providers
 */

import React, { useState, useEffect, useCallback } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { AuthProvider as OidcAuthProvider, useAuth as useOidcAuth } from 'react-oidc-context';
import { GoogleOAuthProvider, GoogleLogin } from '@react-oauth/google';
import { jwtDecode } from 'jwt-decode';

import { config, validateConfig, getOidcConfig } from './config';
import { logger } from './logger';
import { verifyToken, fetchAvailableTools } from './services/api';

import { Icons, AuthCallback, Sidebar, MessageList, InputArea, DictionaryCanvas, ProtestQueryModal, ProtestMonitorConfigModal, VideoSummarizeModal, DeepfakeDetectionModal, DeepResearchModal, StatisticalSummaryModal, InfographicModal, ToolsHelpModal } from './components';
import { useChat } from './hooks';

import './App.css';

// ============================================
// ChatApp Component
// ============================================
function ChatApp() {
  const oidcAuth = useOidcAuth();
  
  // Auth state
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [authProvider, setAuthProvider] = useState(null);
  const [triedSilentAuth, setTriedSilentAuth] = useState(false);
  const [silentAuthInProgress, setSilentAuthInProgress] = useState(false);

  // Tools help state
  const [availableTools, setAvailableTools] = useState([]);
  const [isToolsHelpOpen, setIsToolsHelpOpen] = useState(false);

  // Use the chat hook for all chat-related state and handlers
  const chat = useChat(user, token);

  // ============================================
  // Effects
  // ============================================

  // Validate config on mount
  useEffect(() => {
    const validation = validateConfig();
    if (!validation.isValid) {
      logger.warn('Configuration validation warnings', { errors: validation.errors });
    }
    logger.info('Application initialized', { 
      appName: config.appName,
      modelsCount: config.models.length 
    });
  }, []);

  // Load saved auth on mount (Google OAuth)
  useEffect(() => {
    const savedAuthProvider = localStorage.getItem(config.storageKeys.authProvider);
    
    if (savedAuthProvider === 'google') {
      const savedToken = localStorage.getItem(config.storageKeys.token);
      const savedUser = localStorage.getItem(config.storageKeys.userInfo);
      
      if (savedToken && savedUser) {
        try {
          const parsedUser = JSON.parse(savedUser);
          setToken(savedToken);
          setUser(parsedUser);
          setAuthProvider('google');
          verifyToken(savedToken).then(isValid => {
            if (!isValid) handleLogout();
          });
          fetchAvailableTools(savedToken).then(setAvailableTools);
          logger.info('Restored Google user session', { email: parsedUser.email });
        } catch (error) {
          logger.error('Failed to parse saved user info', error);
          handleLogout();
        }
      }
    }
  }, []);

  // Handle SSO authentication state
  useEffect(() => {
    if (oidcAuth.isAuthenticated && oidcAuth.user) {
      const ssoUser = {
        id: oidcAuth.user.profile?.sub,
        email: oidcAuth.user.profile?.email,
        name: oidcAuth.user.profile?.name || oidcAuth.user.profile?.preferred_username || oidcAuth.user.profile?.email,
        picture: oidcAuth.user.profile?.picture || null,
        roles: oidcAuth.user.profile?.role || [],
      };
      setUser(ssoUser);
      setToken(oidcAuth.user.access_token);
      setAuthProvider('sso');
      localStorage.setItem(config.storageKeys.authProvider, 'sso');
      fetchAvailableTools(oidcAuth.user.access_token).then(setAvailableTools);
      logger.info('SSO user authenticated', { email: ssoUser.email });
    }
  }, [oidcAuth.isAuthenticated, oidcAuth.user]);

  // Automatic SSO redirect on app load
  useEffect(() => {
    const savedAuthProvider = localStorage.getItem(config.storageKeys.authProvider);
    const hasGoogleSession = savedAuthProvider === 'google' && localStorage.getItem(config.storageKeys.token);
    
    if (
      config.identityServerUrl &&
      !oidcAuth.isAuthenticated &&
      !oidcAuth.isLoading &&
      !triedSilentAuth &&
      !hasGoogleSession
    ) {
      setTriedSilentAuth(true);
      setSilentAuthInProgress(true);
      logger.info('Redirecting to SSO login...');
      
      oidcAuth.signinRedirect().catch((error) => {
        logger.error('SSO redirect failed', { error: error.message });
        setSilentAuthInProgress(false);
      });
    }
  }, [oidcAuth.isAuthenticated, oidcAuth.isLoading, triedSilentAuth, oidcAuth]);

  // ============================================
  // Handlers
  // ============================================

  const handleLoginSuccess = (credentialResponse) => {
    try {
      const decoded = jwtDecode(credentialResponse.credential);
      const userInfo = {
        id: decoded.sub,
        email: decoded.email,
        name: decoded.name,
        picture: decoded.picture
      };
      
      setUser(userInfo);
      setToken(credentialResponse.credential);
      setAuthProvider('google');
      
      localStorage.setItem(config.storageKeys.token, credentialResponse.credential);
      localStorage.setItem(config.storageKeys.userInfo, JSON.stringify(userInfo));
      localStorage.setItem(config.storageKeys.authProvider, 'google');

      fetchAvailableTools(credentialResponse.credential).then(setAvailableTools);
      
      logger.userAction('Google Login', { email: userInfo.email });
    } catch (error) {
      logger.error('Failed to process login response', error);
      alert('Login failed. Please try again.');
    }
  };

  const handleSsoLogin = () => {
    logger.userAction('SSO Login initiated');
    oidcAuth.signinRedirect();
  };

  const handleLoginError = () => {
    logger.error('Google login failed');
    alert('Login failed. Please try again.');
  };

  const handleLogout = useCallback(() => {
    const currentProvider = authProvider || localStorage.getItem(config.storageKeys.authProvider);
    logger.userAction('Logout', { email: user?.email, provider: currentProvider });
    
    setUser(null);
    setToken(null);
    setAuthProvider(null);
    setAvailableTools([]);
    setIsToolsHelpOpen(false);
    chat.clearState();
    
    localStorage.removeItem(config.storageKeys.token);
    localStorage.removeItem(config.storageKeys.userInfo);
    localStorage.removeItem(config.storageKeys.authProvider);
    
    if (currentProvider === 'sso' && oidcAuth.isAuthenticated) {
      oidcAuth.signoutRedirect();
    }
  }, [user, authProvider, oidcAuth, chat]);

  const handleSubmitWrapper = (e) => {
    chat.handleSubmit(e, handleLogout);
  };

  const handleKeyDownWrapper = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmitWrapper(e);
    }
  };

  // Unified stop handler: routes to the correct cancel logic based on which loading state is active
  const handleStopWrapper = () => {
    if (chat.isLoading) {
      chat.handleStopChat();
    } else if (chat.hasActiveAsyncJob) {
      chat.handleStopAsyncJob();
    }
  };
  
  // Handle input change with URL detection
  const handleInputChangeWrapper = (value) => {
    chat.handleInputChange(value);
  };

  // ============================================
  // Render: Login Screen
  // ============================================
  if (!user) {
    if (oidcAuth.isLoading || silentAuthInProgress) {
      return (
        <div className="login-container">
          <div className="login-card">
            <h1>{config.appName}</h1>
            <p className="subtitle">{config.appSubtitle}</p>
            <p>{silentAuthInProgress ? 'Checking session...' : 'Loading...'}</p>
            <div className="loading-spinner"></div>
          </div>
        </div>
      );
    }

    return (
      <div className="login-container">
        <div className="login-card">
          <h1>{config.appName}</h1>
          <p className="subtitle">{config.appSubtitle}</p>
          <p>Sign in to continue</p>
          
          {config.identityServerUrl && (
            <div className="sso-login-wrapper">
              <button className="sso-login-btn" onClick={handleSsoLogin}>
                <svg viewBox="0 0 24 24" className="sso-icon">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z"/>
                </svg>
                Sign in with SSO
              </button>
            </div>
          )}
          
          {config.identityServerUrl && config.googleClientId && (
            <div className="auth-divider">
              <span>or</span>
            </div>
          )}
          
          {config.googleClientId && (
            <div className="google-login-wrapper">
              <GoogleLogin
                onSuccess={handleLoginSuccess}
                onError={handleLoginError}
                theme="filled_blue"
                size="large"
                text="signin_with"
                shape="rectangular"
              />
            </div>
          )}
          
          <div className="security-notice">
            <p>Secure | End-to-End Encrypted | Private Network</p>
          </div>
        </div>
      </div>
    );
  }

  // ============================================
  // Render: Main App
  // ============================================
  return (
    <div className={`App ${chat.isDictionaryCanvasOpen ? 'split-view' : ''}`}>
      <Sidebar
        isOpen={chat.isSidebarOpen}
        onClose={() => chat.setIsSidebarOpen(false)}
        conversations={chat.conversations}
        activeConversationId={chat.activeConversationId}
        onNewChat={chat.createNewConversation}
        onSelectConversation={chat.selectConversation}
        onDeleteConversation={chat.deleteConversation}
        onClearAllHistory={chat.handleClearAllHistory}
        user={user}
        onLogout={handleLogout}
      />
      
      <main className="main-content">
        <div className="mobile-header">
          <button 
            className="mobile-menu-btn"
            onClick={() => chat.setIsSidebarOpen(true)}
          >
            <Icons.Menu />
          </button>
          <h1>{config.appName}</h1>
        </div>
        
        <div className="chat-container">
          <MessageList
            messages={chat.messages}
            user={user}
            isLoading={chat.isLoading && !chat.isVideoScoreLoading}
            copiedMessageIdx={chat.copiedMessageIdx}
            expandedCommunications={chat.expandedCommunications}
            onCopyMessage={chat.handleCopyMessage}
            onToggleCommunications={chat.toggleCommunications}
            onReopenCanvas={chat.reopenDictionaryCanvas}
            onRetryAviationArtifacts={chat.retryAviationArtifacts}
              onDownloadAviationPptx={chat.downloadAviationPptx}
            messagesEndRef={chat.messagesEndRef}
          />
        </div>
        
        {/* Dictionary URL Detection Banner */}
        {chat.detectedDictionaryUrl && (
          <div className="dictionary-url-banner">
            <Icons.Dictionary />
            <span>Dictionary URL detected: <strong>{chat.detectedDictionaryUrl.substring(0, 50)}...</strong></span>
          </div>
        )}

        {chat.isVideoScoreLoading && (
          <div className="video-score-status-banner">
            <Icons.Video />
            <span>
              <strong>Video Dictionary Score</strong> is running — download, transcription, and dictionary analysis may take a few minutes.
            </span>
            <span className="video-score-status-spinner" aria-hidden="true" />
          </div>
        )}
        
        <InputArea
          input={chat.input}
          setInput={handleInputChangeWrapper}
          isLoading={chat.isLoading || chat.isVideoScoreLoading}
          selectedImage={chat.selectedImage}
          setSelectedImage={chat.setSelectedImage}
          selectedPdf={chat.selectedPdf}
          setSelectedPdf={chat.setSelectedPdf}
          selectedPdfName={chat.selectedPdfName}
          setSelectedPdfName={chat.setSelectedPdfName}
          selectedExcel={chat.selectedExcel}
          setSelectedExcel={chat.setSelectedExcel}
          selectedExcelName={chat.selectedExcelName}
          setSelectedExcelName={chat.setSelectedExcelName}
          selectedVideo={chat.selectedVideo}
          setSelectedVideo={chat.setSelectedVideo}
          selectedVideoName={chat.selectedVideoName}
          setSelectedVideoName={chat.setSelectedVideoName}
          selectedModel={chat.selectedModel}
          setSelectedModel={chat.setSelectedModel}
          isMultimodal={chat.isMultimodal}
          isUploadDropdownOpen={chat.isUploadDropdownOpen}
          setIsUploadDropdownOpen={chat.setIsUploadDropdownOpen}
          isModelDropdownOpen={chat.isModelDropdownOpen}
          setIsModelDropdownOpen={chat.setIsModelDropdownOpen}
          onSubmit={handleSubmitWrapper}
          onStop={handleStopWrapper}
          isAsyncJobLoading={chat.hasActiveAsyncJob}
          onKeyDown={handleKeyDownWrapper}
          imageInputRef={chat.imageInputRef}
          pdfInputRef={chat.pdfInputRef}
          excelInputRef={chat.excelInputRef}
          videoInputRef={chat.videoInputRef}
          uploadDropdownRef={chat.uploadDropdownRef}
          modelDropdownRef={chat.modelDropdownRef}
          textareaRef={chat.textareaRef}
          onImageChange={chat.handleImageChange}
          onPdfChange={chat.handlePdfChange}
          onExcelChange={chat.handleExcelChange}
          onVideoChange={chat.handleVideoChange}
          onToolsHelpOpen={() => setIsToolsHelpOpen(true)}
        />
      </main>
      
      {/* Dictionary Analysis Canvas */}
      {chat.isDictionaryCanvasOpen && (
        <div className="dictionary-canvas-container">
          <DictionaryCanvas
            isOpen={chat.isDictionaryCanvasOpen}
            onClose={chat.closeDictionaryCanvas}
            imageUrl={chat.dictionaryImageUrl}
            poiName={chat.dictionaryPoiName}
            onPoiNameChange={chat.setDictionaryPoiName}
            onAnalyzePoi={() => chat.startDictionaryAnalysis(chat.dictionaryImageUrl, chat.dictionaryImageData, chat.dictionaryPoiName)}
            results={chat.dictionaryResults}
            edits={chat.dictionaryEdits}
            onUpdateEdit={chat.updateDictionaryEdit}
            onApprove={chat.submitDictionaryApproval}
            isLoading={chat.isDictionaryLoading}
            error={chat.dictionaryError}
            isReadOnly={chat.isCanvasReadOnly}
            questionApprovals={chat.questionApprovals}
            onApproveQuestion={chat.approveQuestion}
            onUnapproveQuestion={chat.unapproveQuestion}
          />
        </div>
      )}
      
      {/* Protest Query Modal */}
      <ProtestQueryModal
        isOpen={chat.isProtestModalOpen}
        onClose={chat.closeProtestModal}
        onSubmit={(...args) => chat.submitProtestQuery(...args, handleLogout)}
        isLoading={chat.isProtestLoading}
        progress={chat.protestProgress}
        initialValues={chat.protestInitialValues}
      />

      <ProtestMonitorConfigModal
        isOpen={chat.isProtestMonitorConfigOpen}
        onClose={chat.closeProtestMonitorConfig}
        onSave={(payload) => chat.saveProtestMonitorConfig(payload, handleLogout)}
        data={chat.protestMonitorConfig}
        isLoading={chat.isProtestMonitorConfigLoading}
        isSaving={chat.isProtestMonitorConfigSaving}
        error={chat.protestMonitorConfigError}
        success={chat.protestMonitorConfigSuccess}
      />
      
      {/* Video Summarize Modal */}
      <VideoSummarizeModal
        isOpen={chat.isVideoModalOpen}
        onClose={chat.closeVideoModal}
        onSubmitFile={chat.submitVideoSummarize}
        onSubmitUrl={chat.submitVideoSummarizeUrl}
        onTranscribeFile={chat.submitVideoTranscribe}
        onTranscribeUrl={chat.submitVideoTranscribeUrl}
        isLoading={chat.isVideoLoading}
        initialValues={chat.videoInitialValues}
        mode={chat.videoMode}
      />

      {/* Deepfake Detection Modal */}
      <DeepfakeDetectionModal
        isOpen={chat.isDeepfakeModalOpen}
        onClose={chat.closeDeepfakeModal}
        onSubmitFile={chat.submitDeepfakeFile}
        onSubmitUrl={chat.submitDeepfakeUrl}
        isLoading={chat.isDeepfakeLoading}
        initialValues={chat.deepfakeInitialValues}
      />

      <DeepResearchModal
        isOpen={chat.isDeepResearchModalOpen}
        onClose={chat.closeDeepResearchModal}
        onSubmit={(params) => chat.submitDeepResearch(params, handleLogout)}
        isLoading={chat.isDeepResearchLoading}
        progress={chat.deepResearchProgress}
        initialValues={chat.deepResearchInitialValues}
        hasPdf={!!chat.selectedPdf}
        hasExcel={!!chat.selectedExcel}
        pdfData={chat.selectedPdf}
        excelData={chat.selectedExcel}
        pdfFilename={chat.selectedPdfName}
        excelFilename={chat.selectedExcelName}
      />

      <StatisticalSummaryModal
        isOpen={chat.isStatisticalSummaryModalOpen}
        onClose={chat.closeStatisticalSummaryModal}
        onSubmit={(params) => chat.submitStatisticalSummary(params, handleLogout)}
        isLoading={chat.isStatisticalSummaryLoading}
      />

      <InfographicModal
        isOpen={chat.isInfographicModalOpen}
        onClose={chat.closeInfographicModal}
        onSubmit={(params) => chat.submitInfographic(params, handleLogout)}
      />

      {/* Tools Help Modal */}
      <ToolsHelpModal
        isOpen={isToolsHelpOpen}
        onClose={() => setIsToolsHelpOpen(false)}
        tools={availableTools}
      />
    </div>
  );
}

// ============================================
// App Content with Routing
// ============================================
function AppContent() {
  return (
    <Routes>
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/*" element={<ChatApp />} />
    </Routes>
  );
}

// ============================================
// App Wrapper with OAuth Providers
// ============================================
function App() {
  if (!config.googleClientId && !config.identityServerUrl) {
    logger.error('No authentication method configured');
    return (
      <div className="login-container">
        <div className="login-card">
          <h1>Configuration Error</h1>
          <p>No authentication method is configured.</p>
          <p className="security-notice">
            Please set either REACT_APP_GOOGLE_CLIENT_ID or REACT_APP_IDENTITY_SERVER_URL.
          </p>
        </div>
      </div>
    );
  }

  const oidcConfig = config.identityServerUrl ? getOidcConfig() : null;

  let app = <AppContent />;

  if (oidcConfig) {
    app = (
      <OidcAuthProvider {...oidcConfig}>
        {app}
      </OidcAuthProvider>
    );
  }

  if (config.googleClientId) {
    app = (
      <GoogleOAuthProvider clientId={config.googleClientId}>
        {app}
      </GoogleOAuthProvider>
    );
  }

  return (
    <BrowserRouter>
      {app}
    </BrowserRouter>
  );
}

export default App;

